/**
 * One visit is one card in Visitantes Ativos.
 *
 * A visit to several destinations is several ROLES — that is what makes each unidade's own list,
 * liberação and revocation work untouched (condo-manager's services/visit_destinations.go
 * explains why). The global active list reads those roles directly, so a courier with parcels
 * for 101, 202 and 303 would arrive as three identical cards and make the counter say
 * "3 visitantes" for one person standing at the gate. The porteiro counts people.
 *
 * Collapsing belongs ONLY to the global list. A unidade's own detail view keeps its single role:
 * unit 202 cares that this visitor is coming to 202, and has no business being shown the other
 * two destinations.
 */

/** The states a visit can be in, most advanced last. */
const STATE_RANK = ['exited', 'consumed', 'awaiting', 'awaiting_entry', 'inside'];

const rankOf = (status: string | undefined): number => {
  const index = STATE_RANK.indexOf(status ?? '');
  return index < 0 ? 0 : index;
};

export interface GroupedVisitorExtras {
  /** Every destination of this visit, in the order the roles came back. */
  destinations?: string[];
  /** The role id of each destination — an action here has to reach a real role. */
  groupRoleIds?: string[];
}

/**
 * Takes the active list's own row shape, whatever it is — the rows carry a dozen fields this has
 * no business knowing about, and only `visitGroupId` decides anything here. Constraining the
 * parameter to that one optional field instead would make every real row "have no properties in
 * common" with it.
 */
export function collapseVisitGroups<T extends Record<string, any>>(
  visitors: T[]
): Array<T & GroupedVisitorExtras> {
  const byGroup = new Map<string, Array<T & Record<string, any>>>();
  const output: Array<T & GroupedVisitorExtras> = [];

  (visitors ?? []).forEach(visitor => {
    const group = (visitor as any).visitGroupId;
    if (!group) {
      output.push(visitor);
      return;
    }
    const existing = byGroup.get(group);
    if (existing) {
      existing.push(visitor as any);
      return;
    }
    const bucket = [visitor as any];
    byGroup.set(group, bucket);
    // The first role of the group holds the card's place in the list, so the order the
    // porteiro sees does not shuffle when a sibling updates.
    output.push(visitor);
  });

  return output.map(visitor => {
    const group = (visitor as any).visitGroupId;
    const bucket = group ? byGroup.get(group) : undefined;
    if (!bucket || bucket.length < 2) return visitor;

    // A half-propagated group (a failed write, a row from before the feature) must read as the
    // most advanced state rather than whichever role happened to come first.
    const leading = bucket.reduce((best, row) => (rankOf(row.status) > rankOf(best.status) ? row : best), bucket[0]);
    const entryTimes = bucket.map(row => row.entryTime).filter(Boolean).sort();

    return {
      ...visitor,
      status: leading.status,
      entryTime: entryTimes[0] ?? (visitor as any).entryTime,
      destinations: bucket.map(row => row.unit).filter(Boolean),
      groupRoleIds: bucket.map(row => row.roleId).filter(Boolean),
    };
  });
}

/**
 * The destinations of ONE visit, read off the person's own roles.
 *
 * The active list is built one row per PERSON — useOperationalVisitors picks that person's
 * newest visitor role — so a visit to three destinations does not arrive as three cards. It
 * arrives as one card naming whichever destination happened to be newest, with the other two
 * invisible. That is worse than a duplicate: the porteiro reads "Entregador — 101" and sends
 * the person away when they ask about 303.
 *
 * Returns [] for an ordinary single-destination visit — the card already shows the unidade the
 * usual way, and a one-item list would just be noise. Inactive siblings are left out: a
 * destination that was revoked is not somewhere this person is going.
 */
export function visitDestinationsOf(
  roles: Array<Record<string, any>> | undefined | null,
  role: Record<string, any> | undefined | null
): string[] {
  const group = role?.role_metadata?.visit_group_id;
  if (!group) return [];

  const siblings = (roles ?? []).filter(
    candidate => candidate?.is_active !== false && candidate?.role_metadata?.visit_group_id === group
  );
  if (siblings.length < 2) return [];

  return siblings
    .map(sibling => {
      const metadata = sibling.role_metadata || {};
      return metadata.unit || metadata.unit_name || metadata.department_name || '';
    })
    .filter(Boolean);
}

/**
 * How a multi-destination visit reads on a card sized for one unidade.
 *
 * "101" alone is a lie when the person is also going to 202 and 303; all three in full blow out
 * the layout. The label names the first and counts the rest, and the tooltip spells it out.
 *
 * Empty for an ordinary visit, so the card stays exactly as it was.
 */
export function visitDestinationsLabel(destinations: string[] | undefined | null): string {
  if (!destinations || destinations.length < 2) return '';
  return `${destinations[0]} +${destinations.length - 1}`;
}

export function visitDestinationsTitle(destinations: string[] | undefined | null): string {
  if (!destinations || destinations.length < 2) return '';
  return `Destinos: ${destinations.join(', ')}`;
}
